import { migrateState } from '../domain/migrate';
import type { WorkspaceState } from '../domain/model';

const STATE_KEY = 'audition-take-preflight:state:v1';
const DB_NAME = 'audition-take-preflight-media-v1';
const STORE = 'raw-media';
const BACKUP_SCHEMA = 'audition-take-preflight-backup-v3';
const LEGACY_BACKUP_SCHEMA = 'audition-take-preflight-backup-v1';

export const initialState: WorkspaceState = {
  schemaVersion: 3, programs: [], takes: [], locks: [], manualChecks: {}, proofs: [], preparations: {},
};

export function loadState(): WorkspaceState {
  try {
    const parsed = JSON.parse(localStorage.getItem(STATE_KEY) ?? '') as WorkspaceState;
    return migrateState(parsed);
  } catch {
    return initialState;
  }
}

export function saveState(state: WorkspaceState): void {
  localStorage.setItem(STATE_KEY, JSON.stringify(state));
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export function createMetadataBackup(state: WorkspaceState): Blob {
  return new Blob([JSON.stringify({
    schema: BACKUP_SCHEMA,
    exportedAt: new Date().toISOString(),
    state,
    rawMediaIncluded: false,
  }, null, 2)], { type: 'application/json' });
}

export async function readMetadataBackup(file: File): Promise<WorkspaceState> {
  const parsed: unknown = JSON.parse(await file.text());
  if (!isRecord(parsed) || ![BACKUP_SCHEMA, LEGACY_BACKUP_SCHEMA, 'audition-take-preflight-backup-v2'].includes(String(parsed.schema)) || parsed.rawMediaIncluded !== false || !isRecord(parsed.state)) {
    throw new Error('unsupported backup');
  }
  const state = parsed.state;
  if (!Array.isArray(state.programs) || !Array.isArray(state.takes) || !Array.isArray(state.locks)
      || !Array.isArray(state.proofs) || !isRecord(state.manualChecks)) {
    throw new Error('invalid backup structure');
  }
  const programsValid = state.programs.every((program) => isRecord(program)
    && typeof program.id === 'string' && typeof program.name === 'string'
    && typeof program.school === 'string' && typeof program.deadline === 'string'
    && typeof program.officialUrl === 'string' && Array.isArray(program.rules));
  const takesValid = state.takes.every((take) => isRecord(take)
    && typeof take.id === 'string' && typeof take.programId === 'string'
    && typeof take.fileName === 'string' && typeof take.hash === 'string');
  if (!programsValid || !takesValid) throw new Error('invalid backup records');
  return migrateState(state);
}

const openDb = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
  const request = indexedDB.open(DB_NAME, 1);
  request.onupgradeneeded = () => request.result.createObjectStore(STORE);
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

export async function storeRawMedia(takeId: string, file: File, hash?: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store=tx.objectStore(STORE);
    if(hash){const key='blob:'+hash;store.put(file,key);store.put({mediaKey:key},takeId);}else store.put(file,takeId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function getRawMedia(takeId: string): Promise<Blob | undefined> {
  const db = await openDb();
  const result = await new Promise<Blob | undefined>((resolve, reject) => {
    const request = db.transaction(STORE).objectStore(STORE).get(takeId);
    request.onsuccess = () => { const value=request.result as Blob|{mediaKey:string}|undefined; if(value&&'mediaKey' in value){const media=db.transaction(STORE).objectStore(STORE).get(value.mediaKey);media.onsuccess=()=>resolve(media.result as Blob|undefined);media.onerror=()=>reject(media.error);}else resolve(value as Blob|undefined); };
    request.onerror = () => reject(request.error);
  });
  db.close();
  return result;
}

export async function deleteRawMedia(takeId: string): Promise<void> { return deleteRawMediaMany([takeId]); }

export type LocalDataDeletionFailure = 'blocked' | 'delete_failed' | 'verification_failed';

export class LocalDataDeletionError extends Error {
  constructor(readonly code: LocalDataDeletionFailure) {
    super(code);
    this.name = 'LocalDataDeletionError';
  }
}

interface DeleteAllLocalDataOptions {
  databaseFactory?: IDBFactory;
  storage?: Pick<Storage, 'removeItem'>;
}

export async function deleteAllLocalData({
  databaseFactory = indexedDB,
  storage = localStorage,
}: DeleteAllLocalDataOptions = {}): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const request = databaseFactory.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = (event) => {
      event.preventDefault();
      reject(new LocalDataDeletionError('delete_failed'));
    };
    request.onblocked = () => reject(new LocalDataDeletionError('blocked'));
  });

  try {
    const databases = await databaseFactory.databases();
    if (databases.some((database) => database.name === DB_NAME)) {
      throw new LocalDataDeletionError('verification_failed');
    }
  } catch (error) {
    if (error instanceof LocalDataDeletionError) throw error;
    throw new LocalDataDeletionError('verification_failed');
  }

  storage.removeItem(STATE_KEY);
  storage.removeItem('atp:first-seen');
  storage.removeItem('atp:last-revisit');
  storage.removeItem('atp:persistence-prompt-shown');
}

/** One transaction: a failed deletion must not leave half of a support record deleted. */
export async function deleteRawMediaMany(ids: string[]): Promise<void> {
 const db=await openDb();
 try { await new Promise<void>((resolve,reject)=>{const tx=db.transaction(STORE,'readwrite');const store=tx.objectStore(STORE);for(const id of ids)store.delete(id);const keys=store.getAllKeys();const values=store.getAll();values.onsuccess=()=>{const keep=new Set((values.result as ({mediaKey?:string}|Blob)[]).flatMap(v=>'mediaKey' in v&&v.mediaKey?[v.mediaKey]:[]));keys.onsuccess=()=>{for(const key of keys.result)if(typeof key==='string'&&key.startsWith('blob:')&&!keep.has(key))store.delete(key);};if(keys.readyState==='done')for(const key of keys.result)if(typeof key==='string'&&key.startsWith('blob:')&&!keep.has(key))store.delete(key);};tx.oncomplete=()=>resolve();tx.onerror=()=>reject(tx.error);tx.onabort=()=>reject(tx.error);}); }
 finally {db.close();}
}

export async function storageStatus() {
 if(typeof navigator==='undefined'||!navigator.storage)return {supported:false as const};
 const estimate=await navigator.storage.estimate?.();const persisted=await navigator.storage.persisted?.();
 return {supported:true as const,usage:estimate?.usage,quota:estimate?.quota,persisted};
}
